import type { TokenGroup } from "../api/types/token.js";
import { findClosestColorToken } from "./color.js";
import { findClosestDimensionToken, parseDimension } from "./dimension.js";
import {
  parseCss,
  parseJs,
  parseJsx,
  parseScss,
  parseTs,
  parseTsx,
  type Match,
} from "./parser.js";

/**
 * Represents a lint violation found in the code.
 */
export interface Violation {
  line: number;
  column: number;
  endLine: number;
  endColumn: number;
  value: string;
  type: "color" | "dimension";
  message: string;
  suggestion?: {
    tokenName: string;
    tokenValue: string;
  };
}

export type Language = "css" | "scss" | "tsx" | "ts" | "js" | "jsx";

/**
 * Lint code against design tokens.
 *
 * @param code - Source code to lint.
 * @param language - Language of the source code.
 * @param tokens - Design tokens to validate against.
 * @param threshold - Color distance threshold for suggestions (default: 10).
 * @returns Array of violations found.
 */
export function lint(
  code: string,
  language: Language,
  tokens: TokenGroup,
  threshold: number = 10,
): Violation[] {
  const violations: Violation[] = [];

  const parsed = (() => {
    switch (language) {
      case "tsx":
        return parseTsx(code);
      case "ts":
        return parseTs(code);
      case "jsx":
        return parseJsx(code);
      case "js":
        return parseJs(code);
      case "scss":
        return parseScss(code);
      case "css":
      default:
        return parseCss(code);
    }
  })();

  for (const match of parsed.colors) {
    const violation = checkColor(match, tokens, threshold);
    if (violation) {
      violations.push(violation);
    }
  }

  for (const match of parsed.dimensions) {
    const violation = checkDimension(match, tokens);
    if (violation) {
      violations.push(violation);
    }
  }

  return violations;
}

/**
 * Check a color match against tokens and return a violation if not using a token.
 */
function checkColor(
  match: Match,
  tokens: TokenGroup,
  threshold: number,
): Violation | null {
  const closest = findClosestColorToken(match.value, tokens, threshold);

  if (!closest) {
    return {
      line: match.line,
      column: match.column,
      endLine: match.endLine,
      endColumn: match.endColumn,
      value: match.value,
      type: "color",
      message: `Color '${match.value}' is not a design token.`,
    };
  }

  const isExactMatch = closest.distance === 0;
  const message = isExactMatch
    ? `Hardcoded color '${match.value}' should use token '${closest.name}'.`
    : `Color '${match.value}' is not a design token. Did you mean '${closest.name}' (${closest.value})?`;

  return {
    line: match.line,
    column: match.column,
    endLine: match.endLine,
    endColumn: match.endColumn,
    value: match.value,
    type: "color",
    message,
    suggestion: {
      tokenName: closest.name,
      tokenValue: closest.value,
    },
  };
}

/**
 * Check a dimension match against tokens and return a violation if not using a token.
 */
function checkDimension(match: Match, tokens: TokenGroup): Violation | null {
  const closest = findClosestDimensionToken(match.value, tokens);

  if (!closest) {
    return {
      line: match.line,
      column: match.column,
      endLine: match.endLine,
      endColumn: match.endColumn,
      value: match.value,
      type: "dimension",
      message: `Dimension '${match.value}' is not a design token.`,
    };
  }

  const parsed = parseDimension(match.value);
  const tokenParsed = parseDimension(closest.value);
  const isExactMatch =
    parsed &&
    tokenParsed &&
    parsed.value === tokenParsed.value &&
    parsed.unit === tokenParsed.unit;

  const message = isExactMatch
    ? `Hardcoded dimension '${match.value}' should use token '${closest.name}'.`
    : `Dimension '${match.value}' is not a design token. Did you mean '${closest.name}' (${closest.value})?`;

  return {
    line: match.line,
    column: match.column,
    endLine: match.endLine,
    endColumn: match.endColumn,
    value: match.value,
    type: "dimension",
    message,
    suggestion: {
      tokenName: closest.name,
      tokenValue: closest.value,
    },
  };
}
